import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CheckCircle, Loader2 } from 'lucide-react';
import confetti from 'canvas-confetti';
import { supabase } from '../../integrations/supabase/client';
const fadeUpVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
export function Waitlist() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'ok' | 'err'>('idle');
  const [busy, setBusy] = useState(false);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const {
        error
      } = await supabase.from('subscriptions').insert([{
        email: email.toLowerCase().trim()
      }]);
      if (error) {
        // Handle duplicate email error specifically
        if (error.code === '23505') {
          setStatus('err');
          console.log('Email already registered');
        } else {
          setStatus('err');
          console.error('Subscription error:', error.message);
        }
      } else {
        setStatus('ok');
        setEmail('');
        // Trigger confetti animation
        confetti({
          particleCount: 100,
          spread: 70,
          origin: {
            y: 0.6
          },
          colors: ['#5E8262', '#6366F1', '#F5F5DC']
        });
      }
    } catch (err) {
      setStatus('err');
      console.error('Unexpected error:', err);
    } finally {
      setBusy(false);
      // Reset status after 5 seconds
      setTimeout(() => setStatus('idle'), 5000);
    }
  };
  return <section className="relative bg-gradient-to-br from-linen-50 via-white to-sage-50/20 py-16 sm:py-20 lg:py-24 overflow-hidden overflow-x-hidden" data-id="element-957">
      {/* Animated background mesh */}
      <div className="absolute inset-0 mesh-gradient opacity-40" data-id="element-958" />
      
      {/* Floating geometric shapes */}
      <motion.div className="absolute top-20 left-10 w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 rounded-full bg-sage-200/20" data-id="element-959" />
      <motion.div className="absolute top-1/2 left-1/3 w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 rotate-45 bg-linen-200/30" data-id="element-960" />
      
      <div className="container px-4 relative z-10" data-id="element-961">
        <motion.div className="grid md:grid-cols-2 gap-8 lg:gap-12 items-center" variants={staggerContainer} initial="hidden" animate="visible" data-id="element-962">
          {/* Image Column */}
          <div className="order-1 md:order-none flex justify-center" data-id="element-963">
            <motion.div variants={fadeUpVariants} className="relative w-full max-w-md" data-id="element-964">
              {/* Decorative circles behind illustration */}
              <motion.div className="absolute top-1/4 left-1/4 w-64 h-64 bg-sage-200/20 rounded-full blur-3xl" data-id="element-965" />
              
              {/* Main illustration with float effect */}
              <motion.img src="https://uploadthingy.s3.us-west-1.amazonaws.com/gBSDbx1E5xa6wLrm3B7jDn/Being_Patient_%281%29.svg" alt="Waiting illustration" className="w-full h-auto relative z-10" data-id="element-966" />
            </motion.div>
          </div>
          {/* Content Column */}
          <div className="space-y-8 text-left" data-id="element-967">

            <motion.div variants={fadeUpVariants} className="inline-flex" data-id="element-968">
              <span className="bg-sage-600 text-linen-50 text-xs font-semibold uppercase tracking-wide px-4 py-2 rounded-full shadow-lg" data-id="element-969">
                Coming Soon
              </span>
            </motion.div>
            
            <motion.h1 variants={fadeUpVariants} className="font-display font-black text-3xl sm:text-4xl lg:text-5xl xl:text-6xl 
                         bg-gradient-to-r from-sage-900 via-sage-700 to-indigo-600 
                         bg-clip-text text-transparent leading-tight" data-id="element-970">
              {['RealtyZen', 'your', 'AI', 'front', 'desk', 'for', 'residential', 'realtors'].map((word, index) => <motion.span key={index} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.3 + index * 0.08,
              duration: 0.5,
              ease: "easeOut"
            }} className="inline-block mr-2 sm:mr-3" data-id="element-971">
                  {word}
                </motion.span>)}
            </motion.h1>
            <motion.p variants={fadeUpVariants} transition={{
            delay: 0.6
          }} className="text-base sm:text-lg text-sage-700 leading-relaxed max-w-lg" data-id="element-972">
              Never miss a deal-making moment again.
              <br data-id="element-973" />
              RealtyZen instantly answers new inquiries, drafts perfect replies,
              and follows up at <em className="text-sage-900 font-medium" data-id="element-974">exactly</em> the right time. So you can
              keep prospects warm while you're out showing homes.
            </motion.p>
            {/* Glassmorphism Form Container */}
            <motion.div variants={fadeUpVariants} transition={{
            delay: 0.8
          }} className="relative max-w-lg" data-id="element-975">
              <motion.div className="glass-card-enhanced rounded-2xl p-6 sm:p-8 transition-all duration-500" data-id="element-976">
                {/* Success State Overlay */}
                {status === 'ok' && <motion.div initial={{
                opacity: 0,
                scale: 0.9
              }} animate={{
                opacity: 1,
                scale: 1
              }} className="absolute inset-0 flex items-center justify-center 
                               bg-white/95 backdrop-blur-sm rounded-2xl z-10" data-id="element-977">
                    <div className="text-center" data-id="element-978">
                      <motion.div initial={{
                    scale: 0
                  }} animate={{
                    scale: 1
                  }} transition={{
                    type: "spring",
                    stiffness: 200
                  }} className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full 
                                   flex items-center justify-center" data-id="element-979">
                        <CheckCircle className="w-8 h-8 text-green-600" data-id="element-980" />
                      </motion.div>
                      <h3 className="text-lg font-semibold text-sage-900 mb-1" data-id="element-981">You're in!</h3>
                      <p className="text-sm text-sage-600" data-id="element-982">Check your email for next steps</p>
                    </div>
                  </motion.div>}
                
                <form onSubmit={handleSubmit} className="space-y-4" data-id="element-983">
                  <div className="space-y-4" data-id="element-984">
                    {/* Enhanced Input */}
                    <div className="relative group" data-id="element-985">
                      <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com" required disabled={busy || status === 'ok'} className="h-14 px-6 text-base input-enhanced
                                   border-2 border-sage-200/50 rounded-xl
                                   focus:border-sage-400 focus:bg-white
                                   transition-all duration-300
                                   placeholder:text-sage-400
                                   disabled:opacity-50" data-id="element-986" />
                      {/* Animated focus indicator */}
                      <motion.div className="absolute -inset-0.5 bg-gradient-to-r from-sage-400 to-indigo-400 
                                   rounded-xl opacity-0 group-focus-within:opacity-20 -z-10 blur" transition={{
                      duration: 0.3
                    }} data-id="element-987" />
                    </div>
                    
                    {/* Enhanced Button */}
                    <Button type="submit" disabled={busy || status === 'ok'} className="w-full h-14 px-8 btn-enhanced
                                 text-white font-semibold text-base rounded-xl
                                 shadow-lg shadow-sage-900/20 hover:shadow-xl hover:shadow-sage-900/30
                                 transform hover:-translate-y-0.5 active:translate-y-0
                                 transition-all duration-200 
                                 relative overflow-hidden group
                                 disabled:opacity-50 disabled:transform-none" data-id="element-988">
                      <span className="absolute inset-0 w-full h-full bg-gradient-to-r 
                                       from-transparent via-white/20 to-transparent 
                                       -translate-x-full group-hover:translate-x-full 
                                       transition-transform duration-700" data-id="element-989" />
                      <span className="relative flex items-center justify-center gap-2" data-id="element-990">
                        {busy ? <Loader2 className="w-5 h-5 animate-spin" data-id="element-991" /> : status === 'ok' ? <CheckCircle className="w-5 h-5" data-id="element-992" /> : null}
                        {busy ? 'Joining…' : status === 'ok' ? 'Joined!' : 'Join Waitlist'}
                      </span>
                    </Button>
                  </div>
                  
                  {/* Error State */}
                  {status === 'err' && <motion.p initial={{
                  opacity: 0,
                  y: -10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} className="text-sm text-red-600 bg-red-50 p-3 rounded-lg border border-red-200" data-id="element-993">
                      Something went wrong. Please try again or use a different email.
                    </motion.p>}
                </form>
                
                {/* Shimmer overlay for glass effect */}
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-transparent 
                                via-white/5 to-transparent -skew-x-12 opacity-0 
                                group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" data-id="element-994" />
              </motion.div>
            </motion.div>

          </div>
        </motion.div>
      </div>
    </section>;
}